import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function ChiesiPatientArchetypesPage() {
  useSEO({
    title: 'Chiesi Patient Archetypes | Design For',
    description: 'Developing patient archetypes to inform pharmaceutical strategy and clinical trial design. Research-driven personas for respiratory disease patient populations.',
    keywords: 'patient archetypes, healthcare personas, pharmaceutical strategy, patient research, clinical trial design',
    canonical: '/work/chiesi-patient-archetypes',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Developing patient archetypes to inform pharmaceutical strategy',
    description: 'We partnered with Chiesi to develop comprehensive patient archetypes based on extensive research with respiratory disease patients. These archetypes inform clinical trial design, patient engagement strategies, and pharmaceutical development.',
    url: 'https://example.com/work/chiesi-patient-archetypes',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global pharmaceutical company - Patient research and archetypes',
    keywords: 'patient archetypes, healthcare personas, pharmaceutical strategy, patient research, clinical trial design, respiratory disease',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Using patient archetypes to shape clinical development
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Global pharmaceutical company</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                Clinical development teams needed a fast, compliant, and meaningful way to truly understand the lived experience of patients participating in trials - beyond protocols and endpoints. We introduced a familiar design tool (behavioural archetypes) into a highly regulated setting and adapted it to work at scale.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Through deep qualitative research across 3 disease areas in the US, we explored what &quot;good&quot; really looks like from a patient perspective and translated this insight into practical tools that could actively shape trial design and decision making.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Conducted 25 in depth qualitative interviews across 3 disease areas',
                  'Tested 17 concepts and experience ideas through rapid prototyping',
                  'Synthesised insight into 12 behavioural patient archetypes and journeys',
                  'Embedded patient insight into live clinical trial design decisions',
                  'Upskilled teams through hands on education in qualitative research',
                  'Presented outcomes at Patients as Partners (London)',
                  'Shifted mindsets within clinical teams toward patient-led decision-making',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/6c69339dc5a77859d2a0cfae4ed16ef8.jpeg"
                  alt="Chiesi patient archetype persona cards"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/19d379b24a086a33375e2a99eac15eb7.png"
                  alt="Patient archetype persona cards showing Safety Net, Digital Active and Disruption Avoider segments"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Footnote */}
          <p className="mt-12 text-xs text-gray-400 italic">
            This work was delivered leading a consulting team during a previous role.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="chiesi-patient-archetypes" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
